import React, { useMemo, useRef } from "react";
import { Billboard, Html } from "@react-three/drei";
import { SPHERE_RADIUS, type MapProgress } from "../LevelMenuIslands";
import { MapStatus } from "../../types/Status.types";
import { useUI } from "../../stores/useUI";

const C = {
  cream: '#FBF2D3',
  dark: '#521616',
  mono: '"IBM Plex Mono", monospace',
};

interface IslandLabelsProps {
  maps: any[];
  getMapStatus: (mapId: string) => MapStatus;
  getMapProgress: (mapId: string) => MapProgress;
  onMapSelect: (mapId: string) => void;
  mapData?: { [mapId: string]: any }; // Added to check connections
}

const IslandLabels = ({ maps, getMapStatus, getMapProgress, onMapSelect, mapData }: IslandLabelsProps) => {
  const hoveredMapId = useUI((state) => state.hoveredMapId);
  const setHoveredMapId = useUI((state) => state.setHoveredMapId);
  const [internalHoveredMapId, setInternalHoveredMapId] = React.useState<string | null>(null);
  
  // Calculate connected maps for the hovered map
  const connectedMapIds = useMemo(() => {
    if (!hoveredMapId || !mapData) return new Set<string>();
    
    const connected = new Set<string>();
    connected.add(hoveredMapId); // Always include the hovered map itself

    // Check outgoing connections from hovered map
    const sourceMap = mapData[hoveredMapId];
    if (sourceMap && sourceMap.npcs) {
      sourceMap.npcs.forEach((npc: any) => {
        if (npc.unlocksMapId) {
          connected.add(npc.unlocksMapId);
        }
      });
    }

    // Check incoming connections to hovered map
    Object.entries(mapData).forEach(([sourceId, map]: [string, any]) => {
      if (map.npcs) {
        map.npcs.forEach((npc: any) => {
          if (npc.unlocksMapId === hoveredMapId) {
            connected.add(sourceId);
          }
        });
      }
    });

    return connected;
  }, [hoveredMapId, mapData]);

  return (
    <>
      {maps.map((map) => {
        const status = getMapStatus(map.id);
        const isVisible = !!hoveredMapId && connectedMapIds.has(map.id);
        const isHovered = internalHoveredMapId === map.id;

        const sphereRadius = SPHERE_RADIUS + (map.model.altitude * SPHERE_RADIUS);
        const sphereCenter = [0, 0, 0];
        const theta = map.model.theta * Math.PI;
        const phi = map.model.phi * Math.PI;

        const x = sphereCenter[0] + sphereRadius * Math.sin(phi) * Math.cos(theta);
        const y = sphereCenter[1] + sphereRadius * Math.cos(phi);
        const z = sphereCenter[2] + sphereRadius * Math.sin(phi) * Math.sin(theta);

        return (
          <Billboard
            key={map.id}
            position={[x, y - 1.2, z]}
            follow={true}
            lockX={false}
            lockY={false}
            lockZ={false}
            
          >
            <Html
              transform
              distanceFactor={10}
              position={[0, 0, 0]}
              center
              style={{
                opacity: isVisible ? 1 : 0, // Hide if not connected/hovered
                transition: 'opacity 0.2s ease-in-out',
                pointerEvents: isVisible ? 'auto' : 'none', // Disable interaction when hidden
              }}
              // occlude
            >
              <div>
                <button
                  style={{
                    background: C.cream,
                    border: `1px solid ${C.dark}`,
                    borderRadius: '93px',
                    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
                    color: C.dark,
                    fontFamily: C.mono,
                    fontSize: '16px',
                    fontWeight: 400,
                    padding: '8px 10px 8px 18px',
                    cursor: status === MapStatus.LOCKED ? 'default' : 'pointer',
                    textAlign: 'center',
                    minWidth: '120px',
                    transition: 'opacity 0.2s ease',
                    opacity: status === MapStatus.LOCKED ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={() => {
                    setHoveredMapId(map.id);
                    setInternalHoveredMapId(map.id);
                  }}
                  onMouseLeave={() => {
                    setHoveredMapId(null);
                    setInternalHoveredMapId(null);
                  }}
                  onClick={status === MapStatus.LOCKED ? undefined : () => onMapSelect(map.id)}
                >
                  {map.name}
                  {status !== MapStatus.LOCKED && (
                    <div style={{
                      width: '28px',
                      height: '28px',
                      padding: '0px 0px 2px 0px',
                      background: C.cream,
                      border: `1px solid ${C.dark}`,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      color: C.dark,
                      flexShrink: 0,
                      transition: 'background 0.2s ease',
                      ...(isHovered ? { background: '#EDE5C0' } : {}),
                    }}>→</div>
                  )}
                </button>
              </div>
            </Html>
          </Billboard>
        );
      })}
    </>
  );
};

export default IslandLabels;
